import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, registerUser } from '../store/authSlice';
import {
  Building2, User, Mail, Phone, Globe, Clock,
  Users, Briefcase, ArrowRight, Eye, EyeOff,
  Check, AlertCircle, ChevronRight,
} from 'lucide-react';
import SecurityCanvas from './SecurityCanvas';
import './Login.css';

const SIZES = ['1–10', '11–50', '51–200', '201–500', '501–1000', '1000+'];
const INDUSTRIES = ['Technology', 'Finance', 'Healthcare', 'Education', 'Manufacturing', 'Retail', 'Government', 'Legal', 'Other'];

function pw_strength(p) {
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[A-Z]/.test(p)) s++;
  if (/[0-9]/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  const map = [['', '#333'], ['Weak', '#EF4444'], ['Fair', '#F97316'], ['Good', '#EAB308'], ['Strong', '#22C55E'], ['Excellent', '#10B981']];
  const [label, color] = map[s] ?? map[0];
  return { score: s, label, color };
}

// Floating label select (modified to match Figma design for select)
function SelectField({ id, label, value, onChange, options, icon }) {
  const isSelected = value !== "";
  return (
    <div className="nm-field" style={{ position: 'relative' }}>
      {icon && (
        <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: isSelected ? '#6B6B6B' : '#FF7A1A', zIndex: 2, pointerEvents: 'none' }}>
          {icon}
        </div>
      )}
      <select
        id={id}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          ...(icon ? { paddingLeft: 40 } : {}),
          paddingTop: '15px',
          paddingBottom: '15px',
          color: isSelected ? 'white' : '#FF7A1A',
          fontWeight: isSelected ? 'normal' : '700',
          fontSize: isSelected ? '14px' : '10px',
          letterSpacing: isSelected ? 'normal' : '0.06em',
          textTransform: isSelected ? 'none' : 'uppercase'
        }}
        required
      >
        <option value="" disabled hidden>{label.toUpperCase()}</option>
        {options.map(o => <option key={o} value={o} style={{ background: '#191919', color: 'white', textTransform: 'none', fontSize: '14px', fontWeight: 'normal', letterSpacing: 'normal' }}>{o}</option>)}
      </select>
      <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: isSelected ? '#6B6B6B' : '#FF7A1A' }}>
        <ChevronRight size={13} style={{ transform: 'rotate(90deg)' }} />
      </div>
    </div>
  );
}

// Step indicator
function StepDot({ active, done, n }) {
  return (
    <div style={{
      width: 26, height: 26, borderRadius: '50%',
      background: done ? '#FF7A1A' : active ? 'rgba(255,122,26,.15)' : 'transparent',
      border: `1.5px solid ${done || active ? '#FF7A1A' : 'rgba(255,255,255,.12)'}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 700,
      color: done ? '#fff' : active ? '#FF7A1A' : '#6B6B6B',
      transition: 'all 300ms ease-in-out',
    }}>
      {done ? <Check size={12} /> : n}
    </div>
  );
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);
  const [f, setF] = useState({
    company: '', orgCode: '', adminName: '', email: '', username: '', phone: '',
    password: '', confirm: '', country: '', timezone: '', size: '', industry: '',
    terms: false,
  });
  const [showPass, setShowPass] = useState(false);
  const [showConf, setShowConf] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState('');

  const set = (k) => (e) =>
    setF(prev => ({ ...prev, [k]: e.target.value }));

  const pw = f.password ? pw_strength(f.password) : null;
  const pwMatch = f.confirm.length > 0 && f.password === f.confirm;

  const addRipple = (e) => {
    const btn = e.currentTarget;
    const r = btn.getBoundingClientRect();
    const s = Math.max(r.width, r.height) * 2;
    const span = document.createElement('span');
    span.className = 'btn-ripple';
    span.style.cssText = `width:${s}px;height:${s}px;left:${e.clientX - r.left - s / 2}px;top:${e.clientY - r.top - s / 2}px;`;
    btn.appendChild(span);
    setTimeout(() => span.remove(), 600);
  };

  const handleSubmit = async (e) => {
    addRipple(e);
    if (!f.terms || !pwMatch) return;

    setApiError('');
    try {
      const registerResult = await dispatch(registerUser({
        username: f.username,
        email: f.email,
        password: f.password,
        adminName: f.adminName,
        company: f.company,
        orgCode: f.orgCode,
        phone: f.phone,
        size: f.size,
        industry: f.industry
      }));

      if (registerUser.fulfilled.match(registerResult)) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      } else {
        setApiError(registerResult.payload || 'Registration failed');
      }
    } catch (err) {
      setApiError('Network error connecting to server');
    }
  };

  const d = (i) => ({ animationDelay: `${300 + i * 60}ms` });

  return (
    <div className="auth-container">

      {/* ── Left ── */}
      <div className="auth-left">
        <SecurityCanvas />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,transparent 65%,#0B0B0C)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,transparent 60%,rgba(11,11,12,.6))', pointerEvents: 'none' }} />

        {/* Top bar */}
        <div style={{ position: 'absolute', top: 28, left: 28, right: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', animation: 'fadeUp 500ms both 300ms' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: '#FF7A1A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#F7F7F7', letterSpacing: '-.01em' }}>DYNAMIC AGENT</span>
          </div>
          <div className="status-pill">
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
            ENTERPRISE
          </div>
        </div>

        {/* Bottom text */}
        <div style={{ position: 'absolute', bottom: 28, left: 28, animation: 'fadeUp 600ms both 800ms' }}>
          <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#F7F7F7', letterSpacing: '-.02em', lineHeight: 1.3 }}>
            Set up your<br />
            <span style={{ color: '#FF7A1A' }}>organization workspace.</span>
          </p>
          <div style={{ display: 'flex', gap: 16, marginTop: 14 }}>
            {[['ISO 27001', '#FF7A1A'], ['SOC 2 Type II', '#FF7A1A'], ['GDPR', '#FF7A1A']].map(([l, c]) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#6B6B6B' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: c }} />
                {l}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right ── */}
      <div className="auth-right">
        <div className="ambient anim-breathe" style={{ position: 'absolute', width: 320, height: 320, top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: 'radial-gradient(circle,rgba(255,122,26,.06) 0%,transparent 70%)', zIndex: 0 }} />

        <div className="nm-card" style={{ width: '100%', maxWidth: 440, padding: '28px 32px', position: 'relative', zIndex: 1, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', overflowX: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 180, height: 1, background: 'linear-gradient(90deg,transparent,rgba(255,122,26,.55),transparent)' }} />


          {/* Header */}
          <div style={{ marginBottom: 22, animation: 'fadeUp 500ms both 450ms' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,122,26,.12)', border: '1px solid rgba(255,122,26,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Building2 size={17} color="#FF7A1A" />
              </div>
              <div>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#F7F7F7', letterSpacing: '-.02em' }}>Create Organization</h1>
                <p style={{ margin: 0, fontSize: 11, color: '#6B6B6B' }}>Setup your enterprise monitoring workspace</p>
              </div>
            </div>
          </div>

          {apiError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.25)', marginBottom: 16, animation: 'fadeUp 300ms both' }}>
              <AlertCircle size={14} color="#EF4444" />
              <span style={{ fontSize: 12, color: '#EF4444', fontWeight: 500 }}>{apiError}</span>
            </div>
          )}

          {/* ── Fields ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Row 1 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, animation: 'fadeUp 500ms both', ...d(0) }}>
              <div className="nm-field">
                <input type="text" placeholder=" " value={f.company} onChange={set('company')} id="rg-co" autoComplete="organization" />
                <label htmlFor="rg-co">Company Name</label>
              </div>
              <div className="nm-field">
                <input type="text" placeholder=" " value={f.orgCode} onChange={set('orgCode')} id="rg-code" autoComplete="off" />
                <label htmlFor="rg-code">Organization Code</label>
              </div>
            </div>

            {/* Row 2 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, animation: 'fadeUp 500ms both', ...d(1) }}>
              <div className="nm-field">
                <input type="text" placeholder=" " value={f.adminName} onChange={set('adminName')} id="rg-admin" autoComplete="name" style={{ paddingLeft: 40 }} />
                <label htmlFor="rg-admin" style={{ left: 40 }}>Admin Name</label>
                <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#6B6B6B', zIndex: 2, pointerEvents: 'none' }}><User size={14} /></div>
              </div>
              <div className="nm-field">
                <input type="email" placeholder=" " value={f.email} onChange={set('email')} id="rg-email" autoComplete="email" style={{ paddingLeft: 40 }} />
                <label htmlFor="rg-email" style={{ left: 40 }}>Admin Email</label>
                <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#6B6B6B', zIndex: 2, pointerEvents: 'none' }}><Mail size={14} /></div>
              </div>
            </div>

            {/* Row 3: Username + Phone */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, animation: 'fadeUp 500ms both', ...d(2) }}>
              <div className="nm-field">
                <input type="text" placeholder=" " value={f.username} onChange={set('username')} id="rg-username" autoComplete="username" style={{ paddingLeft: 40 }} />
                <label htmlFor="rg-username" style={{ left: 40 }}>Username</label>
                <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#6B6B6B', zIndex: 2, pointerEvents: 'none' }}><User size={14} /></div>
              </div>
              <div className="nm-field">
                <input type="tel" placeholder=" " value={f.phone} onChange={set('phone')} id="rg-phone" autoComplete="tel" style={{ paddingLeft: 40 }} />
                <label htmlFor="rg-phone" style={{ left: 40 }}>Phone Number</label>
                <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#6B6B6B', zIndex: 2, pointerEvents: 'none' }}><Phone size={14} /></div>
              </div>
            </div>

            {/* Password */}
            <div className="nm-field" style={{ animation: 'fadeUp 500ms both', ...d(3) }}>
              <input
                type={showPass ? 'text' : 'password'} placeholder=" "
                value={f.password} onChange={set('password')} id="rg-pass"
                style={{ paddingRight: 44 }}
              />
              <label htmlFor="rg-pass">Password</label>
              <button type="button" onClick={() => setShowPass(v => !v)} tabIndex={-1} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6B6B6B', padding: 0, display: 'flex', transition: 'color 300ms' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#FF7A1A')} onMouseLeave={e => (e.currentTarget.style.color = '#6B6B6B')}>
                {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              {/* Strength bar */}
              {pw && (
                <div style={{ position: 'absolute', bottom: -8, left: 0, right: 0, display: 'flex', gap: 3 }}>
                  {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="strength-bar" style={{ flex: 1, height: 2, borderRadius: 2, background: i <= pw.score ? pw.color : 'rgba(255,255,255,.08)' }} />
                  ))}
                </div>
              )}
            </div>
            {pw && <div style={{ fontSize: 11, color: pw.color, fontWeight: 600, marginTop: -4, animation: 'fadeUp 200ms both' }}>{pw.label} password</div>}

            {/* Confirm */}
            <div className="nm-field" style={{ animation: 'fadeUp 500ms both', ...d(4) }}>
              <input
                type={showConf ? 'text' : 'password'} placeholder=" "
                value={f.confirm} onChange={set('confirm')} id="rg-conf"
                style={{
                  paddingRight: 70,
                  borderColor: f.confirm ? (pwMatch ? '#22C55E' : '#EF4444') : undefined,
                }}
              />
              <label htmlFor="rg-conf">Confirm Password</label>
              <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 8, alignItems: 'center' }}>
                {f.confirm && (
                  pwMatch
                    ? <Check size={14} color="#22C55E" />
                    : <AlertCircle size={14} color="#EF4444" />
                )}
                <button type="button" onClick={() => setShowConf(v => !v)} tabIndex={-1} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B6B6B', padding: 0, display: 'flex' }}>
                  {showConf ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {/* Row: Size + Industry */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, animation: 'fadeUp 500ms both', ...d(5) }}>
              <SelectField id="rg-size" label="Company Size" value={f.size} onChange={v => setF(p => ({ ...p, size: v }))} options={SIZES} icon={<Users size={14} />} />
              <SelectField id="rg-ind" label="Industry" value={f.industry} onChange={v => setF(p => ({ ...p, industry: v }))} options={INDUSTRIES} icon={<Briefcase size={14} />} />
            </div>

            {/* Terms */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', animation: 'fadeUp 500ms both', ...d(7) }}>
              <input type="checkbox" className="nm-check" checked={f.terms} onChange={e => setF(p => ({ ...p, terms: e.target.checked }))} style={{ marginTop: 2 }} />
              <span style={{ fontSize: 12, color: '#BDBDBD', lineHeight: 1.5 }}>
                I agree to the{' '}
                <button type="button" className="nm-link" style={{ fontSize: 12 }}>Terms of Service</button>
                {' '}and{' '}
                <button type="button" className="nm-link" style={{ fontSize: 12 }}>Privacy Policy</button>
              </span>
            </label>
          </div>

          {/* Submit */}
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={loading || !f.terms}
            style={{ width: '100%', height: 48, marginTop: 20, fontSize: 14, fontWeight: 600, animation: 'fadeUp 500ms both', animationDelay: '800ms' }}
          >
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spinFast .7s linear infinite' }} />
                Creating workspace…
              </div>
            ) : success ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Check size={16} /> Workspace created!
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                Create Workspace <ArrowRight size={15} />
              </div>
            )}
          </button>

          <p style={{ textAlign: 'center', margin: '16px 0 0', fontSize: 12, color: '#6B6B6B', animation: 'fadeUp 500ms both', animationDelay: '860ms' }}>
            Already have an account?{' '}
            <button type="button" className="nm-link" onClick={() => navigate('/login')}>Sign in</button>
          </p>

          <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: 180, height: 1, background: 'linear-gradient(90deg,transparent,rgba(255,122,26,.3),transparent)' }} />
        </div>
      </div>
    </div>
  );
}
